import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/modules/lib/utils"

const buttonVariants = cva(
  cn(
    "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-3xl whitespace-nowrap transition-all outline-none disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0",
  ),
  {
    variants: {
      variant: {
        primary:
          "bg-linear-105 text-(--button-primary-text-primary) not-disabled:from-interactive-background-primary-gradient-from not-disabled:to-interactive-background-primary-gradient-to hover:bg-linear-to-r hover:from-interactive-background-primary-gradient-hover-from hover:to-interactive-background-primary-gradient-hover-to focus:ring-3 focus:ring-(--button-primary-focus) disabled:bg-(--button-primary-bg-disabled) disabled:text-(--button-primary-text-disabled) disabled:data-[loading=true]:[&_svg]:text-(--button-primary-text-primary)",
        secondary:
          "bg-(--button-secondary-bg-primary) text-(--button-secondary-text-primary) hover:bg-(--button-secondary-bg-primary-hover) focus:ring-3 focus:ring-(--button-secondary-focus-primary) disabled:bg-(--button-secondary-bg-disabled) disabled:text-(--button-secondary-text-disabled) disabled:data-[loading=true]:[&_svg]:text-(--button-secondary-text-primary)",
        secondarySecondary:
          "bg-(--button-secondary-bg-secondary) text-(--button-secondary-text-secondary) hover:bg-(--button-secondary-bg-secondary-hover) focus:ring-3 focus:ring-(--button-secondary-focus-secondary) disabled:bg-(--button-secondary-bg-disabled) disabled:text-(--button-secondary-text-disabled) disabled:data-[loading=true]:[&_svg]:text-(--button-secondary-text-secondary)",
        negativePrimary:
          "bg-(--button-negative-bg-primary) text-(--button-negative-text-primary) hover:bg-(--button-negative-bg-primary-hover) focus:ring-3 focus:ring-(--button-negative-focus) disabled:bg-(--button-negative-bg-disabled) disabled:text-(--button-negative-text-disabled) disabled:data-[loading=true]:[&_svg]:text-(--button-negative-text-secondary)",
        negativeSecondary:
          "bg-(--button-negative-bg-secondary) text-(--button-negative-text-secondary) hover:bg-(--button-negative-bg-secondary-hover) focus:ring-3 focus:ring-(--button-negative-focus) disabled:bg-(--button-negative-bg-disabled) disabled:text-(--button-negative-text-disabled) disabled:data-[loading=true]:[&_svg]:text-(--button-negative-text-secondary)",
        positivePrimary:
          "bg-(--button-positive-bg-primary) text-(--button-positive-text-primary) hover:bg-(--button-positive-bg-primary-hover) focus:ring-3 focus:ring-(--button-positive-focus) disabled:bg-(--button-positive-bg-disabled) disabled:text-(--button-positive-text-disabled) disabled:data-[loading=true]:[&_svg]:text-(--button-positive-text-secondary)",
        positiveSecondary:
          "bg-(--button-positive-bg-secondary) text-(--button-positive-text-secondary) hover:bg-(--button-positive-bg-secondary-hover) focus:border focus:border-borderDefault-hover focus:ring-3 focus:ring-(--button-positive-focus) disabled:bg-(--button-positive-bg-disabled) disabled:text-(--button-positive-text-disabled) disabled:data-[loading=true]:[&_svg]:text-(--button-positive-text-secondary)",
        outline:
          "border border-(--button-outline-border) bg-(--button-outline-bg-primary) text-(--button-outline-text-primary) hover:border-(--button-outline-border-hover) hover:bg-(--button-outline-bg-primary-hover) focus:ring-3 focus:ring-(--button-outline-focus) disabled:bg-(--button-outline-bg-disabled) disabled:text-(--button-outline-text-disabled) disabled:data-[loading=true]:[&_svg]:text-(--button-outline-text-primary)",
        underline:
          "h-fit! min-w-fit! gap-1 rounded-sm p-0! text-(--button-underline-text-primary) underline underline-offset-4 hover:decoration-2 hover:underline-offset-5 focus:ring-2 focus:ring-(--button-underline-focus) disabled:text-(--button-underline-text-disabled) disabled:data-[loading=true]:[&_svg]:text-textOnSurface-subtle",
        ghost:
          "text-(--button-ghost-text) hover:bg-(--button-ghost-bg-hover) hover:text-(--button-ghost-text-hover) focus:ring-2 focus:ring-(--button-ghost-focus) disabled:text-(--button-ghost-text-disabled) disabled:data-[loading=true]:[&_svg]:text-(--button-ghost-text)",
        ghostSecondary:
          "text-(--button-ghost-secondary-text) hover:bg-(--button-ghost-bg-hover) hover:text-(--button-ghost-secondary-text-hover) focus:ring-2 focus:ring-(--button-ghost-secondary-focus) disabled:text-(--button-ghost-secondary-text-disabled) disabled:data-[loading=true]:[&_svg]:text-(--button-ghost-secondary-text)",
        ghostNegative:
          "text-(--button-ghost-negative-text) hover:bg-(--button-ghost-negative-bg-hover) hover:text-(--button-ghost-negative-text-hover) focus:ring-2 focus:ring-(--button-ghost-negative-focus) disabled:text-(--button-ghost-negative-text-disabled) disabled:data-[loading=true]:[&_svg]:text-(--button-ghost-negative-text)",
        ghostPositive:
          "text-(--button-ghost-positive-text) hover:bg-(--button-ghost-positive-bg-hover) hover:text-(--button-ghost-positive-text-hover) focus:ring-2 focus:ring-(--button-ghost-positive-focus) disabled:text-(--button-ghost-positive-text-disabled) disabled:data-[loading=true]:[&_svg]:text-(--button-ghost-positive-text)",
      },
      size: {
        
        xs: "h-7 min-w-20 gap-1 px-2 label-small [&_svg:not([class*='size-'])]:size-3.5",
        sm: "h-8 min-w-20 gap-1.5 px-3 label-small [&_svg:not([class*='size-'])]:size-4",
        md: "h-9 min-w-22 px-4 py-2 label-medium [&_svg:not([class*='size-'])]:size-4.5",
        lg: "h-12 min-w-22 px-5 label-large [&_svg:not([class*='size-'])]:size-5",
        iconLg: "size-12 min-w-fit [&_svg:not([class*='size-'])]:size-6",
        iconMd: "size-9 min-w-fit [&_svg:not([class*='size-'])]:size-5",
        iconSm: "size-8 min-w-fit [&_svg:not([class*='size-'])]:size-4.5",
        iconXs: "size-7 min-w-fit [&_svg:not([class*='size-'])]:size-4",
        iconXxs: "size-5 min-w-fit [&_svg:not([class*='size-'])]:size-3",
      },
    },
    defaultVariants: {
      variant: "secondary",
      size: "md",
    },
  },
)

export type ButtonVariants = VariantProps<typeof buttonVariants>

function Button({
  className,
  variant,
  size,
  isLoading = false,
  type = "button",
  ...props
}: ButtonPrimitive.Props &
  ButtonVariants & {
    isLoading?: boolean
  }) {
  return (
    <ButtonPrimitive
      data-loading={isLoading}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      type={type}
      {...props}
    />
  )
}

export { Button, buttonVariants }
